import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import { useCheckoutStore } from './checkout';
import { useCartStore } from '@/stores/cart';
import { useNuxtApp } from '#imports';
import {
  TY_LAST_ORDER
} from '@/resources/helpers';


export const useOrderStore = defineStore('order', () => {
  const { $logger, $mixpanel } = useNuxtApp();
  const orderId: Ref<Record<string, any>> = ref({});
  const checkoutStore = useCheckoutStore();
  const { personalDetails } = storeToRefs(checkoutStore);
  const cartStore = useCartStore();
  const { cart } = storeToRefs(cartStore);
  const orderConfirmation = ref();
  const order = ref();
  const orderLoading: Ref<boolean> = ref(false);
  const createOrderError = ref(null)
  const confirmOrderError = ref(null)
  const distinctId = computed(() => $mixpanel.get_distinct_id())

  async function createOrder() {
    orderLoading.value = true;
    let street = personalDetails.value.streetAddress;
    let postcode = personalDetails.value?.zipCode ? personalDetails.value?.zipCode : personalDetails.value?.postcode;
    if (personalDetails.value.apartmentNumber) street += ' ' + personalDetails.value.apartmentNumber;

    const address = {
      firstname: personalDetails.value.firstName,
      lastname: personalDetails.value.lastName,
      street: Array.isArray(street) && street?.length ? street : street?.length ? [street] : ['N/A'],
      city: personalDetails.value.city,
      postcode: postcode ? postcode : 'N/A',
      countryId: personalDetails.value.countryId,
      company: undefined,
      taxId: undefined,
      country_code: personalDetails.value?.country_code

    };

    if (personalDetails.value.company) {
      address['company'] = personalDetails.value.company;
    }

    if (personalDetails.value.taxId) {
      address['taxId'] = personalDetails.value.taxId;
    }

    const cartId = await cartStore.getCartId();

    const payload = {
      distinctId: distinctId.value,
      cart_id: cartId,
      addressInformation: {
        shippingAddress: address,
        billingAddress: address
      },
      giftcards: [],
      coupons: [],
      notes: personalDetails.value.notes,
      phoneNumber: personalDetails.value.phoneNumber,
      email: personalDetails.value.email,
      payment_method_code: personalDetails.value.paymentMethod,
      shipping_method_code: '',
      shipping_carrier_code: '',
      products: cart.value.items.map(({ qty, sku, price }) => ({
        qty,
        sku,
        price
      })),
      redirect_host: window.location.origin
    };

    try {
      const response = await $fetch('/api/order/createOrder', {
        method: 'POST',
        body: {
          payload,
          redirect_host: window.location.origin
        }
      });
      order.value = response;
      return response;
    } catch (err) {
      $logger.error('Error in createOrder', err);
      createOrderError.value = err?.toString()
    } finally {
      orderLoading.value = false;
    }
  }

  async function confirmOrder({ cartId, isRetry = false }) {
    // if (!window.localStorage) return;
    // Cache last order in local storage
    if (!import.meta.client) return
    const lsOrder = localStorage.getItem(`${TY_LAST_ORDER}/${cartId}`) || '{}';
    const parsedOrder = JSON.parse(lsOrder);
    const isMatchingOrder = parsedOrder?.cart_id === cartId;
    if (isMatchingOrder && !isRetry) {
      orderConfirmation.value = parsedOrder;
      return orderConfirmation.value;
    }
    try {
      const response = await $fetch('/api/order/confirmOrder', {
        method: 'POST',
        body: { cart_id: cartId }
      });
      orderConfirmation.value = response.result.vsfOrder
      localStorage.setItem(`${TY_LAST_ORDER}/${cartId}`, JSON.stringify(response.result.vsfOrder));
      return response;
    } catch (err) {
      $logger.error('Error in confirmOrder store', err);
      confirmOrderError.value = err
    }
  }

  const clearOrder = () => {
    order.value = null;
  };

  return {
    createOrder,
    confirmOrder,
    orderConfirmation,
    clearOrder,
    order,
    orderLoading,
    orderId,
    createOrderError,
    confirmOrderError
  };
});
